import { useState } from "react";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, Divider, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useContext } from "react";
import { UserContext } from "src/Utils/Context/UserContext";
import { useSendPasswordResetEmail } from "src/data/account/owner";
import { useTranslation } from "react-i18next";
import { apiUrl } from "src/helpers/urls";

const TEAL = "#2D9B8F";
const ICON_INACTIVE = "#6B7280";

export default function SideBarUserMenu({ leftDrawerOpened }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, setUser } = useContext(UserContext);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState(false);

  const { refetch: sendPasswordResetEmail } = useSendPasswordResetEmail(user?.email);

  const initials = user
    ? `${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}`.toUpperCase()
    : "";

  const handleLogout = async () => {
    setMenuAnchor(null);
    try {
      const logoutUrl = `${apiUrl}/logout`;
      await fetch(logoutUrl, { method: 'POST', credentials: 'include' });
    } catch (e) {
      console.log("logout error", e);
    }
    setUser(null);
    navigate("/login");
  };

  const handleChangePassword = async () => {
    setMenuAnchor(null);
    try {
      await sendPasswordResetEmail();
      setResetSuccess(true);
    } catch {
      setResetError(true);
    }
  };

  return (
    <>

      <Divider style={{ backgroundColor: "#edf2f7", height: "1px", width: '100%' }} />
      <Box
        onClick={(e) => setMenuAnchor(e.currentTarget)}
        sx={{
          display: "flex",
          alignItems: "center",
          px: 1.5,
          py: 1.5,
          gap: 1.5,
          minHeight: 64,
          cursor: "pointer",
          borderRadius: 2,
          mx: 0.5,
          mb: 0.5,
          ":hover": { bgcolor: "#F9FAFB" },
        }}
      >

        <Avatar
          sx={{
            width: 36,
            height: 36,
            bgcolor: TEAL,
            fontSize: 14,
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {initials}
        </Avatar>
        {leftDrawerOpened && user && (
          <>
            <Box sx={{ overflow: "hidden", flexGrow: 1 }}>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#111827",
                  lineHeight: 1.3,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {user.firstName} {user.lastName}
              </Typography>
              <Typography sx={{ fontSize: 12, color: ICON_INACTIVE, lineHeight: 1.3 }}>
                Owner
              </Typography>
            </Box>
            <MoreVertIcon sx={{ color: ICON_INACTIVE, fontSize: 18, flexShrink: 0 }} />
          </>
        )}
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        onClick={() => setMenuAnchor(null)}
        transformOrigin={{ horizontal: "left", vertical: "bottom" }}
        anchorOrigin={{ horizontal: "left", vertical: "top" }}
        slotProps={{ paper: { elevation: 2, sx: { minWidth: 180, borderRadius: 2 } } }}
      >
        <MenuItem onClick={handleChangePassword} sx={{ fontSize: 14, gap: 1.5 }}>
          <SettingsIcon fontSize="small" sx={{ color: ICON_INACTIVE }} />
          {t("profileDropDown.changePassword")}
        </MenuItem>
        <MenuItem onClick={handleLogout} sx={{ fontSize: 14, gap: 1.5, color: "#EF4444" }}>
          <LogoutIcon fontSize="small" sx={{ color: "#EF4444" }} />
          {t("profileDropDown.logout")}
        </MenuItem>
      </Menu>

      <Snackbar open={resetSuccess} autoHideDuration={6000} onClose={() => setResetSuccess(false)}>
        <MuiAlert onClose={() => setResetSuccess(false)} severity="success" sx={{ width: "100%" }} elevation={6} variant="filled">
          {t("profileDropDown.emailSent")}
        </MuiAlert>
      </Snackbar>
      <Snackbar open={resetError} autoHideDuration={6000} onClose={() => setResetError(false)}>
        <MuiAlert onClose={() => setResetError(false)} severity="error" sx={{ width: "100%" }} elevation={6} variant="filled">
          There was a problem, please try again or contact us if it persists.
        </MuiAlert>
      </Snackbar>
    </>
  );
}
